import { useDoc } from '@/store/documentStore'
import { useUi } from '@/store/uiStore'
import { getSource } from './sources'

type Dest = string | unknown[] | null

/** Resolve a pdf.js destination of a given source to [pageIndex in Folio's page list, top in points]. */
export async function resolveDest(
  srcId: string,
  dest: Dest,
): Promise<{ index: number; top?: number } | null> {
  const src = getSource(srcId)
  if (!src || !dest) return null
  let explicit: unknown = dest
  if (typeof dest === 'string') explicit = await src.pdf.getDestination(dest)
  if (!Array.isArray(explicit)) return null
  const [ref, kind, ...args] = explicit as [unknown, { name?: string } | undefined, ...(number | null)[]]
  let srcIndex: number
  if (typeof ref === 'number') srcIndex = ref
  else if (ref && typeof ref === 'object') srcIndex = await src.pdf.getPageIndex(ref as never)
  else return null
  const pages = useDoc.getState().pages
  const index = pages.findIndex((p) => p.srcId === srcId && p.srcIndex === srcIndex)
  if (index < 0) return null
  const page = pages[index]
  let top: number | undefined
  // XYZ left top zoom / FitH top / FitBH top
  const yUser =
    kind?.name === 'XYZ' ? args[1] : kind?.name === 'FitH' || kind?.name === 'FitBH' ? args[0] : null
  if (typeof yUser === 'number' && page.baseRotation === 0) top = Math.max(0, page.view[3] - yUser)
  return { index, top }
}

/** Minimal implementation of pdf.js' IPDFLinkService used by the annotation layer. */
export function createLinkService(srcId: string) {
  return {
    externalLinkEnabled: true,
    eventBus: null,
    get pagesCount() {
      return useDoc.getState().pages.length
    },
    get page() {
      return useUi.getState().currentPage + 1
    },
    set page(n: number) {
      useUi.getState().goToPage(n - 1)
    },
    rotation: 0,
    isInPresentationMode: false,
    async goToDestination(dest: Dest) {
      const r = await resolveDest(srcId, dest)
      if (r) useUi.getState().goToPage(r.index, r.top)
    },
    goToPage(n: number) {
      useUi.getState().goToPage(n - 1)
    },
    addLinkAttributes(link: HTMLAnchorElement, url: string, newWindow = true) {
      if (!/^(https?:|mailto:|tel:)/i.test(url)) return
      link.href = url
      link.title = url
      link.target = newWindow ? '_blank' : '_blank'
      link.rel = 'noopener noreferrer nofollow'
    },
    getDestinationHash: (dest: Dest) => (typeof dest === 'string' ? `#${encodeURIComponent(dest)}` : '#'),
    getAnchorUrl: (hash: string) => `#${hash}`,
    setHash() {},
    executeNamedAction(action: string) {
      const ui = useUi.getState()
      const n = useDoc.getState().pages.length
      if (action === 'NextPage') ui.goToPage(Math.min(n - 1, ui.currentPage + 1))
      else if (action === 'PrevPage') ui.goToPage(Math.max(0, ui.currentPage - 1))
      else if (action === 'FirstPage') ui.goToPage(0)
      else if (action === 'LastPage') ui.goToPage(n - 1)
    },
    executeSetOCGState() {},
    getAttachmentContent: async () => null,
    cachePageRef() {},
    isPageVisible: () => true,
    isPageCached: () => true,
  }
}
